import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Typography, Statistic, Tag, Tabs, Table, Button, Space,
  Switch, InputNumber, Form, Modal, Input, Popconfirm, Spin, Badge, message,
  Checkbox, Empty, List, Divider,
} from 'antd';
import {
  DollarOutlined, ThunderboltOutlined, BarChartOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
  PlusOutlined, EditOutlined, DeleteOutlined,
  CheckOutlined, GiftOutlined, SendOutlined,
} from '@ant-design/icons';
import { billingAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatTokens(n) {
  if (n == null) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  return n.toLocaleString();
}

function formatNumber(n) {
  if (n == null) return '0';
  return Number(n).toLocaleString();
}

const statusColors = { active: 'green', pending: 'orange', cancelled: 'red', none: 'default' };
const txTypeColors = { purchase: 'cyan', subscription: 'blue', auto_topup: 'geekblue', usage: 'orange', refund: 'green', admin_grant: 'purple' };
const txStatusColors = { pending: 'gold', approved: 'green', rejected: 'red', completed: 'blue' };

// ---------------------------------------------------------------------------
// Tab 1: Balance
// ---------------------------------------------------------------------------
function BalanceTab() {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoTopup, setAutoTopup] = useState({ enabled: false, credits_amount: 100, threshold: 10 });
  const [saving, setSaving] = useState(false);

  const fetchBalance = useCallback(async () => {
    try {
      const res = await billingAPI.getBalance();
      setBalance(res.data);
      if (res.data.auto_topup) {
        setAutoTopup(res.data.auto_topup);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  const handleSaveTopup = async () => {
    setSaving(true);
    try {
      await billingAPI.updateAutoTopup(autoTopup);
      message.success('Auto top-up 設定已儲存');
      fetchBalance();
    } catch (e) {
      message.error(e.message || '儲存失敗');
    }
    setSaving(false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;
  if (!balance) return <Empty description="無法載入餘額資料" />;

  const subStatus = balance.subscription_status || 'none';

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Warebit 餘額"
              value={formatNumber(balance.credits_balance)}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="可用 Token 數"
              value={formatTokens(balance.tokens_balance)}
              prefix={<ThunderboltOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="累計已使用 Token"
              value={formatTokens(balance.tokens_used_total)}
              prefix={<BarChartOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginTop: 16 }} title="訂閱方案">
        <Space>
          <Text strong>{balance.plan_name || '無訂閱方案'}</Text>
          <Tag color={statusColors[subStatus] || 'default'}>{subStatus}</Tag>
        </Space>
        <Divider style={{ margin: '12px 0' }} />
        <Space>
          <Button type="primary" icon={<GiftOutlined />} onClick={() => { const t = document.querySelector('.ant-tabs-tab:nth-child(2)'); if(t) t.click(); }}>
            兌換優惠券
          </Button>
          <Button icon={<SendOutlined />} onClick={() => window.location.href = '/coupon-request'}>
            申請免費優惠券
          </Button>
        </Space>
      </Card>

      <Card style={{ marginTop: 16 }} title="Auto Top-up 設定">
        <Form layout="inline">
          <Form.Item label="啟用">
            <Switch
              checked={autoTopup.enabled}
              onChange={(v) => setAutoTopup(prev => ({ ...prev, enabled: v }))}
            />
          </Form.Item>
          <Form.Item label="充值 Warebit">
            <InputNumber
              min={1}
              value={autoTopup.credits_amount}
              onChange={(v) => setAutoTopup(prev => ({ ...prev, credits_amount: v }))}
              style={{ width: 120 }}
            />
          </Form.Item>
          <Form.Item label="低於門檻">
            <InputNumber
              min={0}
              value={autoTopup.threshold}
              onChange={(v) => setAutoTopup(prev => ({ ...prev, threshold: v }))}
              style={{ width: 120 }}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" loading={saving} onClick={handleSaveTopup}>儲存</Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2: Plans
// ---------------------------------------------------------------------------
function CouponTab() {
  const [customerToken, setCustomerToken] = useState(() => localStorage.getItem('alayaess_customer_token') || '');
  const [couponCode, setCouponCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState(null);

  const handleRedeem = async () => {
    if (!customerToken.trim()) {
      message.warning('請先輸入 AlayaEss Customer Token');
      return;
    }
    if (!couponCode.trim()) {
      message.warning('請輸入優惠券代碼');
      return;
    }
    localStorage.setItem('alayaess_customer_token', customerToken.trim());
    setRedeeming(true);
    setResult(null);
    try {
      const res = await billingAPI.redeemCoupon(couponCode.trim(), customerToken.trim());
      setResult({ ok: true, message: res.data.message, tokens: res.data.tokens });
      message.success('兌換成功！');
    } catch (e) {
      setResult({ ok: false, message: e.message || '兌換失敗' });
    }
    setRedeeming(false);
  };

  return (
    <Card title="兌換優惠券">
      <div style={{ marginBottom: 16 }}>
        <Text>有優惠碼？輸入下方即可兌換。</Text>
      </div>

      {/* Customer Token */}
      <div style={{ marginBottom: 12 }}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
          AlayaEss Customer Token（
          <a href="https://alayaess.comboware.tw/customer" target="_blank" rel="noopener noreferrer">
            前往登入
          </a>
          {' '}→ F12 → Console → sessionStorage.getItem('customerToken')）
        </Text>
        <Input.Password
          placeholder="輸入你的 AlayaEss Customer Token"
          value={customerToken}
          onChange={(e) => {
            setCustomerToken(e.target.value);
            localStorage.setItem('alayaess_customer_token', e.target.value);
          }}
          size="large"
          style={{ maxWidth: 500 }}
        />
      </div>

      {/* Coupon Code */}
      <Space.Compact style={{ width: '100%', maxWidth: 500 }}>
        <Input
          placeholder="輸入 Coupon 代碼"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          onPressEnter={handleRedeem}
          size="large"
          prefix={<GiftOutlined />}
        />
        <Button type="primary" onClick={handleRedeem} loading={redeeming} size="large">
          兌換
        </Button>
      </Space.Compact>
      {result && (
        <div style={{ marginTop: 16 }}>
          {result.ok ? (
            <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
              <Space>
                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                <div>
                  <Text strong style={{ color: '#52c41a' }}>兌換成功！</Text>
                  <br />
                  <Text>{result.message}</Text>
                  {result.tokens > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
                        + {result.tokens.toLocaleString()} Tokens
                      </Tag>
                    </div>
                  )}
                </div>
              </Space>
            </Card>
          ) : (
            <Card size="small" style={{ background: '#fff2f0', borderColor: '#ffccc7' }}>
              <Space>
                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />
                <div>
                  <Text strong style={{ color: '#ff4d4f' }}>兌換失敗</Text>
                  <br />
                  <Text>{result.message}</Text>
                </div>
              </Space>
            </Card>
          )}
        </div>
      )}
      <Divider />
      <div>
        <Text type="secondary">
          優惠券由 AlayaEss 訂閱平台提供。管理員可至{' '}
          <a href="https://alayaess.comboware.tw/portal" target="_blank" rel="noopener noreferrer">
            alayaess.comboware.tw/portal
          </a>{' '}
          建立與管理優惠券。
        </Text>
      </div>
    </Card>
  );
}


function PlansTab({ isAdmin }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [form] = Form.useForm();

  const fetchPlans = useCallback(async () => {
    try {
      const res = isAdmin ? await billingAPI.listAllPlans() : await billingAPI.listPlans();
      setPlans(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const handleSubscribe = async (planId) => {
    try {
      await billingAPI.subscribe(planId);
      message.success('訂閱成功');
      fetchPlans();
    } catch (e) {
      message.error(e.message || '訂閱失敗');
    }
  };

  const openCreate = () => {
    setEditPlan(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (plan) => {
    setEditPlan(plan);
    form.setFieldsValue({
      ...plan,
      features: plan.features ? plan.features.join('\n') : '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const data = {
        ...values,
        features: values.features ? values.features.split('\n').filter(Boolean) : [],
      };
      if (editPlan) {
        await billingAPI.updatePlan(editPlan.id, data);
        message.success('方案已更新');
      } else {
        await billingAPI.createPlan(data);
        message.success('方案已建立');
      }
      setModalOpen(false);
      fetchPlans();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  };

  const handleDelete = async (planId) => {
    try {
      await billingAPI.deletePlan(planId);
      message.success('方案已刪除');
      fetchPlans();
    } catch (e) {
      message.error(e.message || '刪除失敗');
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;

  return (
    <div>
      {isAdmin && (
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>管理方案</Button>
        </div>
      )}
      <Row gutter={[16, 16]}>
        {plans.map(plan => (
          <Col xs={24} sm={12} lg={8} key={plan.id}>
            <Badge.Ribbon text="推薦" color="volcano" style={{ display: plan.is_popular ? 'block' : 'none' }}>
              <Card
                title={plan.name}
                extra={isAdmin && (
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(plan)} />
                    <Popconfirm title="確定刪除？" onConfirm={() => handleDelete(plan.id)}>
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                )}
                style={{ height: '100%' }}
              >
                {plan.description && <Paragraph type="secondary">{plan.description}</Paragraph>}
                <Statistic
                  title="月費"
                  value={plan.price || 0}
                  prefix="USD"
                  suffix="/月"
                  valueStyle={{ color: '#722ed1', fontSize: 28 }}
                />
                <Statistic
                  title="每月 Warebit"
                  value={formatNumber(plan.credits_per_month)}
                  suffix="Warebit/月"
                  style={{ marginTop: 8 }}
                />
                {plan.features && plan.features.length > 0 && (
                  <List
                    size="small"
                    style={{ marginTop: 12 }}
                    dataSource={plan.features}
                    renderItem={(f) => (
                      <List.Item style={{ padding: '4px 0', border: 'none' }}>
                        <CheckOutlined style={{ color: '#52c41a', marginRight: 8 }} />{f}
                      </List.Item>
                    )}
                  />
                )}
                <div style={{ marginTop: 16 }}>
                  <Popconfirm title={`確定訂閱「${plan.name}」？`} onConfirm={() => handleSubscribe(plan.id)}>
                    <Button type="primary" block>訂閱</Button>
                  </Popconfirm>
                </div>
              </Card>
            </Badge.Ribbon>
          </Col>
        ))}
        {plans.length === 0 && (
          <Col span={24}><Empty description="尚無可用方案" /></Col>
        )}
      </Row>

      <Modal
        title={editPlan ? '編輯方案' : '新增方案'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="儲存"
        cancelText="取消"
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="方案名稱" rules={[{ required: true, message: '請輸入方案名稱' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="price" label="月費 (USD)" rules={[{ required: true, message: '請輸入價格' }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="credits_per_month" label="每月 Warebit" rules={[{ required: true, message: '請輸入 Warebit' }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="is_popular" label="推薦" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="features" label="功能特色 (每行一項)">
            <Input.TextArea rows={4} placeholder="每行輸入一項功能" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3: Usage Report
// ---------------------------------------------------------------------------
function UsageTab({ isAdmin }) {
  const [summary, setSummary] = useState(null);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [sumRes, usageRes] = await Promise.all([
        billingAPI.getUsageSummary(),
        isAdmin ? billingAPI.getAllUsage() : billingAPI.getUsage(),
      ]);
      setSummary(sumRes.data);
      setUsage((usageRes.data && usageRes.data.items) || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    { title: '時間', dataIndex: 'created_at', key: 'time', width: 180, render: (v) => v ? new Date(v).toLocaleString('zh-TW') : '-' },
    ...(isAdmin ? [{ title: '使用者', dataIndex: 'username', key: 'username', width: 120 }] : []),
    { title: '說明', dataIndex: 'description', key: 'desc', ellipsis: true },
    { title: 'Prompt 字元', dataIndex: 'prompt_chars', key: 'prompt_chars', width: 110, render: (v) => formatNumber(v) },
    { title: '估算 Token', dataIndex: 'estimated_tokens', key: 'tokens', width: 110, render: (v) => formatTokens(v) },
    { title: 'Warebit 成本', dataIndex: 'credits_cost', key: 'cost', width: 110, render: (v) => v != null ? v.toFixed(2) : '-' },
  ];

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;

  return (
    <div>
      {summary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <Card><Statistic title="總 Token 使用" value={formatTokens(summary.total_tokens_used)} prefix={<ThunderboltOutlined />} /></Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card><Statistic title="Warebit 成本" value={summary.total_credits_cost != null ? summary.total_credits_cost.toFixed(2) : '0'} prefix={<DollarOutlined />} /></Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card><Statistic title="AI 呼叫次數" value={formatNumber(summary.total_usage_records)} prefix={<BarChartOutlined />} /></Card>
          </Col>
        </Row>
      )}
      <Card title="用量明細">
        <Table
          dataSource={usage}
          columns={columns}
          rowKey={(r, i) => r.id || i}
          size="small"
          pagination={{ pageSize: 20 }}
          scroll={{ x: 700 }}
        />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 4: Transactions
// ---------------------------------------------------------------------------
function TransactionsTab({ isAdmin }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = isAdmin ? await billingAPI.getAllTransactions() : await billingAPI.getTransactions();
      setTransactions((res.data && res.data.items) || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const columns = [
    { title: '日期', dataIndex: 'created_at', key: 'date', width: 170, render: (v) => v ? new Date(v).toLocaleString('zh-TW') : '-' },
    ...(isAdmin ? [{ title: '使用者', dataIndex: 'username', key: 'username', width: 100 }] : []),
    {
      title: '類型', dataIndex: 'type', key: 'type', width: 120,
      render: (v) => <Tag color={txTypeColors[v] || 'default'}>{v}</Tag>,
    },
    { title: '說明', dataIndex: 'description', key: 'desc', ellipsis: true },
    {
      title: 'Warebit', dataIndex: 'credits', key: 'credits', width: 100,
      render: (v) => {
        if (v == null) return '-';
        const color = v >= 0 ? '#52c41a' : '#f5222d';
        return <Text style={{ color }}>{v >= 0 ? '+' : ''}{formatNumber(v)}</Text>;
      },
    },
    { title: 'Tokens', dataIndex: 'tokens', key: 'tokens', width: 100, render: (v) => formatTokens(v) },
    {
      title: '狀態', dataIndex: 'status', key: 'status', width: 90,
      render: (v) => <Tag color={txStatusColors[v] || 'default'}>{v}</Tag>,
    },
  ];

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;

  return (
    <Card title="交易記錄">
      <Table
        dataSource={transactions}
        columns={columns}
        rowKey={(r, i) => r.id || i}
        size="small"
        pagination={{ pageSize: 20 }}
        scroll={{ x: 700 }}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tab 5: Admin
// ---------------------------------------------------------------------------
function AdminTab() {
  const [pending, setPending] = useState([]);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [grantModal, setGrantModal] = useState(false);
  const [grantForm] = Form.useForm();
  const [configForm] = Form.useForm();

  const fetchData = useCallback(async () => {
    try {
      const [cfgRes, pendRes, balRes] = await Promise.all([
        billingAPI.getConfig(),
        billingAPI.getPending(),
        billingAPI.getAllBalances(),
      ]);
      configForm.setFieldsValue(cfgRes.data || {});
      setPending(pendRes.data || []);
      setBalances(balRes.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [configForm]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const values = await configForm.validateFields();
      await billingAPI.updateConfig(values);
      message.success('設定已儲存');
      fetchData();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
    setSaving(false);
  };

  const handleApprove = async (id, action) => {
    try {
      await billingAPI.approveTransaction(id, action);
      message.success(action === 'approve' ? '已核准' : '已拒絕');
      fetchData();
    } catch (e) {
      message.error(e.message || '操作失敗');
    }
  };

  const handleGrant = async () => {
    try {
      const values = await grantForm.validateFields();
      await billingAPI.grantCredits(values);
      message.success('已發放 Warebit');
      setGrantModal(false);
      grantForm.resetFields();
      fetchData();
    } catch (e) {
      if (e.message) message.error(e.message);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin /></div>;

  const pendingColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '使用者', dataIndex: 'username', key: 'username', width: 120 },
    { title: '類型', dataIndex: 'type', key: 'type', width: 100, render: (v) => <Tag color={txTypeColors[v] || 'default'}>{v}</Tag> },
    { title: '說明', dataIndex: 'description', key: 'desc', ellipsis: true },
    { title: 'Warebit', dataIndex: 'credits', key: 'credits', width: 80 },
    {
      title: '操作', key: 'action', width: 150,
      render: (_, r) => (
        <Space>
          <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => handleApprove(r.id, 'approve')}>核准</Button>
          <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => handleApprove(r.id, 'reject')}>拒絕</Button>
        </Space>
      ),
    },
  ];

  const balanceColumns = [
    { title: '使用者', dataIndex: 'username', key: 'username', width: 140 },
    { title: 'Warebit 餘額', dataIndex: 'credits_balance', key: 'credits', width: 120, render: (v) => formatNumber(v) },
    { title: 'Token 餘額', dataIndex: 'tokens_balance', key: 'tokens', width: 120, render: (v) => formatTokens(v) },
    { title: '已用 Token', dataIndex: 'tokens_used_total', key: 'used', width: 120, render: (v) => formatTokens(v) },
    {
      title: '訂閱狀態', dataIndex: 'subscription_status', key: 'sub', width: 100,
      render: (v) => <Tag color={statusColors[v] || 'default'}>{v || 'none'}</Tag>,
    },
  ];

  return (
    <div>
      <Card title="帳務系統設定" style={{ marginBottom: 16 }} extra={<Button type="primary" loading={saving} onClick={handleSaveConfig}>儲存設定</Button>}>
        <Form form={configForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="credit_name" label="點數名稱">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="credit_value_usd" label="每點價值 (USD)">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="tokens_per_credit" label="每點 Token 數">
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="auto_approve" label="自動核准" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="initial_credits" label="註冊贈送 Warebit">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="billing_terms_text" label="使用條款">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Card>

      <Card
        title="待審核交易"
        style={{ marginBottom: 16 }}
        extra={<Button icon={<GiftOutlined />} onClick={() => setGrantModal(true)}>發放 Warebit</Button>}
      >
        <Table
          dataSource={pending}
          columns={pendingColumns}
          rowKey={(r) => r.id}
          size="small"
          pagination={false}
          locale={{ emptyText: '無待審核項目' }}
        />
      </Card>

      <Card title="所有用戶餘額">
        <Table
          dataSource={balances}
          columns={balanceColumns}
          rowKey={(r, i) => r.user_id || i}
          size="small"
          pagination={{ pageSize: 20 }}
          scroll={{ x: 600 }}
        />
      </Card>

      <Modal
        title="手動發放 Warebit"
        open={grantModal}
        onOk={handleGrant}
        onCancel={() => setGrantModal(false)}
        okText="發放"
        cancelText="取消"
      >
        <Form form={grantForm} layout="vertical">
          <Form.Item name="user_id" label="用戶 ID" rules={[{ required: true, message: '請輸入用戶 ID' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="credits" label="Warebit 數量" rules={[{ required: true, message: '請輸入數量' }]}>
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="description" label="說明">
            <Input placeholder="例：活動贈送" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main BillingPage
// ---------------------------------------------------------------------------
export default function BillingPage({ currentUser }) {
  const isAdmin = currentUser?.role === 'admin';
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsModal, setTermsModal] = useState(false);
  const [termsText, setTermsText] = useState('');
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await billingAPI.getStatus();
        const data = res.data;
        if (!data.has_accepted_terms) {
          setTermsText(data.billing_terms_text || '');
          setTermsModal(true);
        }
      } catch { /* ignore */ }
      setStatusLoading(false);
    })();
  }, []);

  const handleAcceptTerms = async () => {
    try {
      const res = await billingAPI.acceptTerms();
      setTermsModal(false);
      const credits = res.data?.initial_credits;
      if (credits) {
        message.success(`歡迎！已贈送 ${credits} Warebit`);
      } else {
        message.success('使用條款已接受');
      }
    } catch (e) {
      message.error(e.message || '接受條款失敗');
    }
  };

  const tabItems = [
    { key: 'balance', label: '餘額', children: <BalanceTab /> },
    { key: 'coupon', label: '優惠券', children: <CouponTab /> },
    { key: 'plans', label: '方案', children: <PlansTab isAdmin={isAdmin} /> },
    { key: 'usage', label: '用量報表', children: <UsageTab isAdmin={isAdmin} /> },
    { key: 'transactions', label: '交易記錄', children: <TransactionsTab isAdmin={isAdmin} /> },
  ];

  if (isAdmin) {
    tabItems.push({ key: 'admin', label: '管理', children: <AdminTab /> });
  }

  return (
    <div>
      {/* Hero Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #722ed1 0%, #1890ff 100%)',
        borderRadius: 16, padding: '36px 40px', marginBottom: 24,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -30, right: -30, width: 180, height: 180,
          borderRadius: '50%', background: 'rgba(255,255,255,0.08)',
        }} />
        <Row align="middle">
          <Col flex="auto">
            <Title level={2} style={{ color: '#fff', margin: 0 }}>
              帳務中心
            </Title>
            <Paragraph style={{ color: 'rgba(255,255,255,0.75)', margin: '8px 0 0', fontSize: 15 }}>
              管理餘額、訂閱方案、用量報表與交易記錄
            </Paragraph>
          </Col>
          <Col>
            <DollarOutlined style={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
          </Col>
        </Row>
      </div>

      {statusLoading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : (
        <Card>
          <Tabs items={tabItems} />
        </Card>
      )}

      {/* Terms Modal */}
      <Modal
        title="歡迎使用帳務系統"
        open={termsModal}
        closable={false}
        maskClosable={false}
        footer={[
          <Button
            key="accept"
            type="primary"
            disabled={!termsChecked}
            onClick={handleAcceptTerms}
          >
            確認
          </Button>,
        ]}
      >
        <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 8 }}>
          <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{termsText || '歡迎使用本帳務系統。使用本系統表示您同意相關服務條款。'}</Paragraph>
        </div>
        <Checkbox checked={termsChecked} onChange={(e) => setTermsChecked(e.target.checked)}>
          我同意以上使用條款
        </Checkbox>
      </Modal>
    </div>
  );
}
